/**
 * One photograph, from the frames on hand to the phone taken out.
 *
 *   1. Pick the frame — the sharpest of the last few, because a shutter press
 *      shakes the phone and the frame under the thumb is rarely the best one.
 *   2. Find the phone — pose first, then the detector over each hand.
 *   3. Erase it — unless it covers the face, which is left alone and said so.
 *   4. Measure the tilt, for straightening at compose time.
 *
 * There used to be a multi-frame rebuild here: align a buffer of frames, find
 * what moved, and copy what was behind the phone out of earlier frames. On
 * real mirror selfies it could not work — what is behind a held phone is the
 * person holding it, who moves with it, so no earlier frame ever saw it clean.
 * Measured, it recovered 39–56% of the hole at best. A learned inpainter on a
 * well-made mask does better on every photograph tested, and needs one frame.
 */
import { erasePhones } from '../erase/erase';
import { findPhones, type Scene } from '../erase/find';
import type { Phone } from '../erase/geometry';
import { prepareEraser, type EraserInfo } from '../ml/inpaint';
import { grayOf, sharpnessOf } from '../vision/image';
import { estimateTilt } from '../vision/tilt';

/** Width the tilt and sharpness are measured at; more resolution buys nothing. */
const MEASURE_WIDTH = 260;

export type Source = 'camera' | 'photo' | 'clip';

export interface CaptureResult {
  /** The frame as the camera saw it. Never modified. */
  original: HTMLCanvasElement;
  /** The working photograph: automatic erasing, then any brush strokes. */
  erased: HTMLCanvasElement;
  scene: Scene;
  /** Phones taken out automatically. */
  removed: Phone[];
  /** Phones left in because they hide the face. */
  refused: Phone[];
  /** How many brush strokes have been applied since. */
  strokes: number;
  tiltDegrees: number;
  eraser: EraserInfo | null;
  source: Source;
  takenAt: number;
  ms: { find: number; fill: number };
}

export type CaptureStage = { value: number; label: string };

/**
 * The sharpest of `frames`, by mean gradient on a small grey copy.
 * Cheap enough to run over a whole burst: about a millisecond a frame.
 */
export function sharpest(frames: readonly HTMLCanvasElement[]): HTMLCanvasElement {
  let best = frames[frames.length - 1];
  let bestScore = -1;
  for (const f of frames) {
    const s = sharpnessOf(grayOf(f, MEASURE_WIDTH));
    if (s > bestScore) {
      bestScore = s;
      best = f;
    }
  }
  return best;
}

function copy(source: HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = source.width;
  c.height = source.height;
  c.getContext('2d')!.drawImage(source, 0, 0);
  return c;
}

export interface ProcessOptions {
  source: Source;
  takenAt?: number;
  onStage?: (stage: CaptureStage) => void;
  /** Report model download progress, the first time the eraser is needed. */
  onDownload?: (loaded: number, total: number) => void;
}

export async function processFrame(frame: HTMLCanvasElement, opts: ProcessOptions): Promise<CaptureResult> {
  const stage = opts.onStage ?? (() => {});
  const original = copy(frame);

  stage({ value: 0.1, label: 'Finding your phone' });
  const scene = await findPhones(original);

  let erased = copy(original);
  let removed: Phone[] = [];
  let refused: Phone[] = [];
  let eraser: EraserInfo | null = null;
  let fillMs = 0;

  if (scene.phones.length > 0) {
    stage({ value: 0.3, label: 'Getting the eraser ready' });
    eraser = await prepareEraser((loaded, total) => {
      opts.onDownload?.(loaded, total);
      stage({ value: 0.3 + 0.3 * (loaded / total), label: `Downloading the eraser — once — ${Math.round((100 * loaded) / total)}%` });
    });
    stage({ value: 0.6, label: 'Taking the phone out' });
    const result = await erasePhones(original, scene);
    erased = result.canvas === original ? erased : result.canvas;
    removed = result.erased;
    refused = result.refused;
    fillMs = result.ms.fill;
  }

  stage({ value: 0.95, label: 'Straightening' });
  const tiltDegrees = estimateTilt(grayOf(erased, MEASURE_WIDTH));
  stage({ value: 1, label: 'Done' });

  return {
    original,
    erased,
    scene,
    removed,
    refused,
    strokes: 0,
    tiltDegrees,
    eraser,
    source: opts.source,
    takenAt: opts.takenAt ?? Date.now(),
    ms: { find: scene.ms, fill: fillMs },
  };
}
